/**
 * Klaviyo Error Parser Module (Story 12.7, Task 2)
 *
 * Maps Klaviyo API error responses to standardized error types.
 *
 * Klaviyo API error response shape (JSON:API format):
 * { errors: [{ id, status, code, title, detail, source, meta }] }
 *
 * See: https://developers.klaviyo.com/en/docs/authenticate_
 */

// ============================================================================
// Types
// ============================================================================

export type KlaviyoErrorType =
  | "token_expired"
  | "permission_denied"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown";

export interface KlaviyoApiError {
  type: KlaviyoErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface KlaviyoErrorEntry {
  id?: string;
  status?: number;
  code?: string;
  title?: string;
  detail?: string;
  source?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface KlaviyoErrorResponse {
  errors?: KlaviyoErrorEntry[];
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<number, { type: KlaviyoErrorType; retryable: boolean }> = {
  401: { type: "token_expired", retryable: false },
  403: { type: "permission_denied", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// Error Parser (Task 2.4)
// ============================================================================

/**
 * Parses a Klaviyo API error response into a standardized error type.
 *
 * @param responseBody - The parsed JSON body from a Klaviyo API response
 * @param httpStatus - The HTTP status code from the response
 * @returns A standardized Klaviyo API error, or null if no error detected
 */
export function parseKlaviyoApiError(
  responseBody: unknown,
  httpStatus?: number
): KlaviyoApiError | null {
  const body = responseBody as KlaviyoErrorResponse;
  const firstError = body?.errors?.[0];

  const status = httpStatus ?? (firstError?.status ? Number(firstError.status) : null);

  if (!status || (status >= 200 && status < 300)) {
    return null;
  }

  const message = firstError?.detail || firstError?.title || `Klaviyo API error: HTTP ${status}`;
  const mapping = STATUS_CODE_MAP[status];

  if (mapping) {
    return {
      type: mapping.type,
      message,
      status,
      retryable: mapping.retryable,
    };
  }

  return {
    type: "unknown",
    message,
    status,
    retryable: false,
  };
}

// ============================================================================
// Story 12.8: Reporting API Quota Exhaustion
// ============================================================================

/**
 * Thrown when the Klaviyo Reporting API daily quota counter has reached the
 * configured hard limit (200/225). Callers should catch this specifically and
 * degrade gracefully (serve cached data or skip Reporting API records), rather
 * than treating it as a generic error.
 *
 * Why: the Reporting API daily cap (225/day) is the most restrictive rate limit
 * in the entire integration suite. Hitting it blocks every tenant on the same
 * OAuth app instance until midnight UTC. A cheap KV counter + early short-circuit
 * protects against quota exhaustion at our current scale (see ADR note in
 * klaviyo.ts `fetchMetrics()` JSDoc). See `klaviyo/api.ts` helpers
 * `getReportingQuotaUsage`, `incrementReportingQuota`, `checkReportingQuotaBudget`.
 */
export class KlaviyoQuotaExhaustedError extends Error {
  readonly count: number;
  readonly limit: number;

  constructor(count: number, limit: number) {
    super(
      `Klaviyo Reporting API daily quota exhausted (${count}/${limit} calls used today). ` +
      `Serving cached data. Quota resets at midnight UTC.`
    );
    this.name = "KlaviyoQuotaExhaustedError";
    this.count = count;
    this.limit = limit;
  }
}
